import {useCallback, useEffect, useMemo} from "react";
import {DataSource} from "../../models/models";
import {DocumentDataResponse} from "../../models/collections.models";
import {useConversation} from "../../contexts/ConversationContext";
import {AttachedSourceDocGroup, useSelectedFiles} from "../../contexts/SelectedFilesContext";
import useDataSources from "../../hooks/useDataSources";
import useGroupDocuments, {GroupPaginationState} from "./useGroupDocuments";

interface UseChatDataSourceDocumentsType {
    isLoadingDataSources: boolean;
    dataSources: DataSource[];
    itemsByGroup: Record<string, DocumentDataResponse[]>;
    loadingGroupIds: (number | string)[];
    selectedGroupIds: Array<number>;
    sortedSelectedDataSources: Array<DataSource>;
    selectedDocIds: Array<number>;
    pageSizeOptions: number[];
    getGroupPagination: (groupId: number) => GroupPaginationState;
    handleLoadGroupItems: (groupId: number, currentPage: number, pageSize: number, search?: string) => Promise<void>;
    getSelectedCountInSource: (sourceId: number) => number;
    handleDataSourceSelect: (ids: number[]) => void;
    handleConfirmDocumentsSelection: (onClose: (isCancelled: boolean) => void) => void;
    handlePageChange: (groupId: number, newPage: number) => void;
    handlePageSizeChange: (groupId: number, newSize: number) => void;
    handleSearchChange: (groupId: number, newSearch: string) => void;
}


export const useChatDataSourceDocuments = (): UseChatDataSourceDocumentsType => {
    const {dataSources, isLoadingDataSources} = useDataSources();

    const {
        itemsByGroup,
        loadingGroupIds,
        pageSizeOptions,
        getGroupPagination,
        loadGroupItems,
        handlePageChange,
        handlePageSizeChange,
        handleSearchChange,
    } = useGroupDocuments();

    const {state, updateSourcesFromUser} = useConversation();
    const {selectedGroupIds, selectedDocIds} = state;
    const {setAttachedSourceCollections, setAttachedSourceDocs} = useSelectedFiles();


    useEffect(() => {
        selectedGroupIds.forEach(groupId => {
            if (!itemsByGroup[String(groupId)] && !loadingGroupIds.includes(groupId)) {
                const { page, pageSize, search } = getGroupPagination(groupId);
                void loadGroupItems(groupId, page, pageSize, search);
            }
        });
    }, [selectedGroupIds, itemsByGroup, loadingGroupIds]);

    const selectedDataSources = useMemo(() => {
        return dataSources.filter(ds => selectedGroupIds.includes(ds.id));
    }, [dataSources, selectedGroupIds]);


    const sortedSelectedDataSources = useMemo(() => {
        return [...selectedDataSources].sort((a, b) => {
            const docsA = itemsByGroup[String(a.id)] || [];
            const docsB = itemsByGroup[String(b.id)] || [];

            if (docsA.length > 0 && docsB.length === 0) return -1;
            if (docsA.length === 0 && docsB.length > 0) return 1;
            return 0;
        });
    }, [selectedDataSources, itemsByGroup]);

    // TODO: tu można dodać docsy do zaznaczenia jeśli collection jest default i potrzebne widoczne wszystkie docs
    const getSelectedCountInSource = useCallback((sourceId: number) => {
        const docs = itemsByGroup[String(sourceId)] || [];
        return docs.filter(doc => selectedDocIds.includes(doc.id)).length;
    }, [itemsByGroup, selectedDocIds]);

    const handleDataSourceSelect = useCallback((ids: number[]) => {
        const newGroupIds = ids.filter(id =>
            dataSources.some(ds => ds.id === id)
        );

        newGroupIds.forEach(groupId => {
            if (!itemsByGroup[String(groupId)] && !loadingGroupIds.includes(groupId)) {
                const { page, pageSize, search } = getGroupPagination(groupId);
                void loadGroupItems(groupId, page, pageSize, search);
            }
        });

        // wybieram grupe z multiselect i pobieram dane dokumentów w source
        // dokumenty w: itemsByGroup, paginacja, pageSize
        updateSourcesFromUser(newGroupIds, []);
    }, [dataSources, getGroupPagination, itemsByGroup, loadGroupItems, loadingGroupIds, updateSourcesFromUser]);



    // TODO: przebudowa, jeśli będzie zmiana na wiele kolekcji i wiele dokumentów z tych kolekcji
    // być może trzeba będzie zgrać to lepiej z SelectedFilesContext
    const handleConfirmDocumentsSelection = (onClose: (isCancelled: boolean) => void) => {
        const currentSelectedDocIds = state.selectedDocIds;

        const selectedCollections = state.selectedWholeCollectionIds.map(colId => dataSources.find(src => src.id === colId));
        const isWholeCollectionSelected = selectedCollections.length > 0;

        // kolekcja SelectFilesContext
        if (selectedCollections.length > 0 && isWholeCollectionSelected) {
            setAttachedSourceCollections(selectedCollections.map(c => ({
                id: c?.id ?? 0,
                name: c?.name ?? '',
                count: c?.amountOfDocuments ?? 0,
            })))

            onClose(false);
            return;
        }

        // dokument z kolekcji SelectFilesContext
        if (currentSelectedDocIds != null && currentSelectedDocIds.length > 0) {
            const selectedDocGroups: AttachedSourceDocGroup[] = dataSources
                .filter(source => state.selectedGroupIds.includes(source.id))
                .map(source => {
                    return {
                        sourceId: source.id,
                        sourceName: source.name,
                        docIds: currentSelectedDocIds,
                    };
                })
                .filter((group): group is AttachedSourceDocGroup => group !== null);

            setAttachedSourceDocs(currentSelectedDocIds, selectedDocGroups);
        }

        onClose(false);
    };

    return {
        isLoadingDataSources,

        dataSources,
        itemsByGroup,
        loadingGroupIds,
        selectedGroupIds,
        sortedSelectedDataSources,
        selectedDocIds,

        pageSizeOptions,

        getGroupPagination,
        handleLoadGroupItems: loadGroupItems,

        getSelectedCountInSource,
        handleDataSourceSelect,
        handleConfirmDocumentsSelection,

        handlePageChange,
        handlePageSizeChange,
        handleSearchChange,
    };
};